import { useState, useEffect } from "react";
import { BookOpen, Download, RefreshCw, Filter, Loader2, X, FileSpreadsheet, FileText, Eye } from "lucide-react";
import { exportarTablaPdf, fechaReporte } from "../utils/reportes";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import SelectBuscable from "../components/SelectBuscable";   // Fix EH (2026-09-25)
import { useLocation } from "react-router-dom";

// ─── Column definitions ───────────────────────────────────────────────────────
const GESTION_COLS = [
  { key: "fecha_oc",        label: "FECHA OC",          width: "w-[96px]",  align: "left",  mono: false },
  { key: "orden_compra",    label: "ORDEN DE COMPRA",    width: "w-[160px]", align: "left",  mono: true  },
  // Fix HQ (2026-10-23): "En este informe agregar «proveedor»" (CEN, Informe de Compras).
  { key: "proveedor",       label: "PROVEEDOR",          width: "w-[170px]", align: "left",  mono: false },
  { key: "descripcion_item",label: "DESCRIPCIÓN ÍTEM",   width: "w-[220px]", align: "left",  mono: false },
  { key: "unidad_medida",   label: "UN. MED.",           width: "w-[72px]",  align: "center",mono: false },
  { key: "cantidad",        label: "CANT.",              width: "w-[72px]",  align: "right", mono: true,  number: true },
  { key: "subtotal",        label: "SUBTOTAL",           width: "w-[120px]", align: "right", mono: true,  currency: true },
  { key: "sector",          label: "SECTOR",             width: "w-[80px]",  align: "left",  mono: false },
  { key: "frente",          label: "FRENTE",             width: "w-[100px]", align: "left",  mono: false },
  { key: "cod_cliente",     label: "COD",                width: "w-[80px]",  align: "left",  mono: true  },
  { key: "cliente",         label: "CLIENTE",            width: "w-[140px]", align: "left",  mono: false },
  { key: "obra",            label: "OBRA",               width: "w-[160px]", align: "left",  mono: false },
  { key: "presupuesto",     label: "PRESUPUESTO",        width: "w-[120px]", align: "left",  mono: true  },
];

const CONTABLE_COLS = [
  { key: "fecha_factura",    label: "FECHA FAC.",         width: "w-[96px]",  align: "left",  mono: false },
  { key: "nro_factura",      label: "NRO FACTURA",        width: "w-[120px]", align: "left",  mono: true  },
  { key: "timbrado",         label: "TIMBRADO",           width: "w-[100px]", align: "left",  mono: true  },
  { key: "rsocial",          label: "R. SOCIAL / NOMBRE", width: "w-[180px]", align: "left",  mono: false },
  { key: "ruc",              label: "RUC",                width: "w-[100px]", align: "left",  mono: true  },
  { key: "grav_10",          label: "Grav. 10%",          width: "w-[110px]", align: "right", mono: true,  currency: true },
  { key: "grav_5",           label: "Grav. 5%",           width: "w-[100px]", align: "right", mono: true,  currency: true },
  { key: "iva_10",           label: "IVA 10%",            width: "w-[100px]", align: "right", mono: true,  currency: true },
  { key: "iva_5",            label: "IVA 5%",             width: "w-[100px]", align: "right", mono: true,  currency: true },
  { key: "exento",           label: "Exento",             width: "w-[100px]", align: "right", mono: true,  currency: true },
  { key: "bas_imp",          label: "Bas. Imp",           width: "w-[100px]", align: "right", mono: true,  currency: true },
  { key: "no_imputable",     label: "No Imput.",          width: "w-[100px]", align: "right", mono: true,  currency: true },
  { key: "total_fisica",     label: "Total",              width: "w-[120px]", align: "right", mono: true,  currency: true },
  { key: "tipo_comprobante", label: "Tipo Cpte.",         width: "w-[100px]", align: "center",mono: false },
  { key: "clasf_export",     label: "Clasf.Exp.",         width: "w-[80px]",  align: "center",mono: false },
  { key: "rubro_export",     label: "Rubro.Exp.",         width: "w-[80px]",  align: "center",mono: false },
  { key: "venc_timbrado",    label: "Venc.Timb.",         width: "w-[90px]",  align: "center",mono: false },
  { key: "condicion",        label: "Condición",          width: "w-[90px]",  align: "center",mono: false },
  { key: "tipo_doc",         label: "Tipo Doc.",          width: "w-[90px]",  align: "center",mono: false },
];

// Fix FA (2026-10-06): "En Informe de Compras deberá quedar la presentación hasta los Datos de
// Gestión Interna. Y eliminemos Sector y Frente. Agregar una última columna donde se visualice
// si dicho ítem ya tiene factura vinculada" · "En Libro de Compras... solo los Datos Contables"
// (CEN).
const INFORME_COLS = [...GESTION_COLS.filter(c => !["sector", "frente"].includes(c.key)),
  { key: "factura_vinculada", label: "FACTURA VINCULADA", width: "w-[150px]", align: "left", mono: false }];

const ALL_COLS = [...INFORME_COLS, ...CONTABLE_COLS];

// Fix EB (2026-09-23): el PDF lleva las columnas que entran en una hoja.
const PDF_LIBRO = ["fecha_factura", "nro_factura", "timbrado", "rsocial", "ruc",
  "grav_10", "iva_10", "grav_5", "iva_5", "exento", "total_fisica", "condicion"];
const PDF_INFORME = ["fecha_oc", "orden_compra", "proveedor", "descripcion_item", "unidad_medida", "cantidad",   // Fix HQ: + proveedor
  "subtotal", "cliente", "obra", "factura_vinculada"];

const TIPO_COLORS = {
  "OC Materiales": "bg-blue-100 text-blue-700",
  "OTS Servicios": "bg-purple-100 text-purple-700",
  "Caja Chica":    "bg-amber-100 text-amber-700",
};

function fmtNum(n) {
  if (!n && n !== 0) return "—";
  if (n === 0) return "—";
  return Number(n).toLocaleString("es-PY");
}

function fmtDate(d) {
  if (!d) return "—";
  return d;
}

// ─── Main Component ───────────────────────────────────────────────────────────
const MONTHS = [
  { value: "01", label: "Enero" }, { value: "02", label: "Febrero" },
  { value: "03", label: "Marzo" }, { value: "04", label: "Abril" },
  { value: "05", label: "Mayo" }, { value: "06", label: "Junio" },
  { value: "07", label: "Julio" }, { value: "08", label: "Agosto" },
  { value: "09", label: "Septiembre" }, { value: "10", label: "Octubre" },
  { value: "11", label: "Noviembre" }, { value: "12", label: "Diciembre" },
];

export default function ComprasLibroCompras() {
  const { user } = useAuth();
  const [entries, setEntries]     = useState([]);
  const [projects, setProjects]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters]     = useState({ desde: "", hasta: "", project_id: "", tipo: "", cod_cliente: "", item_text: "", nro_factura: "", timbrado: "" });
  // Fix EB (2026-09-23): los filtros con que se cargó la tabla. El Excel y el PDF salen con
  // estos, no con lo que esté a medio escribir sin "Aplicar".
  const [aplicados, setAplicados] = useState({});
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear]   = useState("");
  const opts = { withCredentials: true };
  // Fix EZ (2026-10-05): "Cambiar el nombre al que está en compras como «Informe de Compras» y
  // en Contabilidad que quede «Libro de Compras»" (CEN). El de Contabilidad no lleva los gastos
  // de caja chica sin factura.
  const esLibro = useLocation().pathname.startsWith("/contabilidad");
  const titulo = esLibro ? "Libro de Compras" : "Informe de Compras";
  const archivoBase = esLibro ? "Libro_Compras" : "Informe_Compras";
  const COLS = esLibro ? CONTABLE_COLS : INFORME_COLS;   // Fix FA (2026-10-06)

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 4 }, (_, i) => currentYear - i);

  const applyMonthFilter = (month, year) => {
    if (!month || !year) return;
    const y = year;
    const m = month;
    const lastDay = new Date(Number(y), Number(m), 0).getDate();
    setFilters(f => ({ ...f, desde: `${y}-${m}-01`, hasta: `${y}-${m}-${String(lastDay).padStart(2, "0")}` }));
  };

  const fetchData = async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.desde)      params.desde      = f.desde;
      if (f.hasta)      params.hasta      = f.hasta;
      if (f.project_id) params.project_id = f.project_id;
      if (f.tipo)       params.tipo       = f.tipo;
      if (f.cod_cliente) params.cod_cliente = f.cod_cliente;
      if (f.item_text)  params.item_text  = f.item_text;
      if (f.nro_factura) params.nro_factura = f.nro_factura;
      if (f.timbrado)   params.timbrado   = f.timbrado;
      if (esLibro)      params.solo_facturas = true;   // Fix EZ
      const [libroRes, projRes] = await Promise.all([
        axios.get(`${API}/purchases/libro-compras`,  { ...opts, params }),
        axios.get(`${API}/projects`, opts),
      ]);
      setEntries(libroRes.data || []);
      setProjects(projRes.data || []);
      setAplicados(f);
    } catch {
      toast.error(`Error al cargar el ${titulo.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const a = aplicados;
      const params = new URLSearchParams();
      if (a.desde)      params.append("desde",      a.desde);
      if (a.hasta)      params.append("hasta",      a.hasta);
      if (a.project_id) params.append("project_id", a.project_id);
      if (a.tipo)       params.append("tipo",       a.tipo);
      if (a.cod_cliente) params.append("cod_cliente", a.cod_cliente);
      if (a.item_text)  params.append("item_text",  a.item_text);
      if (a.nro_factura) params.append("nro_factura", a.nro_factura);
      if (a.timbrado)   params.append("timbrado",   a.timbrado);
      if (esLibro)      params.append("solo_facturas", "true");   // Fix EZ
      const res = await axios.get(
        `${API}/purchases/libro-compras/export-excel?${params}`,
        { ...opts, responseType: "blob" }
      );
      const url  = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href  = url;
      link.download = `${archivoBase}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Excel generado correctamente");
    } catch {
      toast.error("Error al exportar Excel");
    } finally {
      setExporting(false);
    }
  };

  // Fix EB (2026-09-23): PDF de lo que muestra la tabla, con sus filtros.
  const handleExportPdf = (ver = false) => {   // Fix FO: ver = vista previa en pantalla
    const cols = (esLibro ? PDF_LIBRO : PDF_INFORME).map(k => ALL_COLS.find(c => c.key === k));   // Fix FA
    const a = aplicados;
    const tipos = { oc_materiales: "OC Materiales", ots_servicios: "OTS Servicios", caja_chica: "Caja Chica" };
    exportarTablaPdf({
      titulo,
      filtros: [a.desde && `Desde ${fechaReporte(a.desde)}`, a.hasta && `Hasta ${fechaReporte(a.hasta)}`,
        a.project_id && `Obra ${projects.find(p => p._id === a.project_id)?.name || ""}`, a.tipo && `Tipo ${tipos[a.tipo] || a.tipo}`,
        a.cod_cliente && `Cliente ${a.cod_cliente}`, a.item_text && `Ítem "${a.item_text}"`,
        a.nro_factura && `Factura ${a.nro_factura}`, a.timbrado && `Timbrado ${a.timbrado}`].filter(Boolean).join(" · "),
      columnas: cols.map(c => ({ l: c.label, num: !!c.currency })),
      filas: entries.map(e => cols.map(c => e[c.key])),
      totales: cols.map((c, i) => (i === 0 ? "TOTALES" : c.currency ? entries.reduce((s, e) => s + (Number(e[c.key]) || 0), 0) : "")),
      archivo: `${archivoBase}_${new Date().toISOString().slice(0, 10)}.pdf`,
      ver,
    });
  };

  // Totals
  const totals = entries.reduce((acc, e) => {
    acc.subtotal    += e.subtotal     || 0;
    acc.grav_10     += e.grav_10      || 0;
    acc.grav_5      += e.grav_5       || 0;
    acc.iva_10      += e.iva_10       || 0;
    acc.iva_5       += e.iva_5        || 0;
    acc.exento      += e.exento       || 0;
    acc.bas_imp     += e.bas_imp      || 0;
    acc.no_imputable+= e.no_imputable || 0;
    acc.total_fisica+= e.total_fisica || 0;
    return acc;
  }, { subtotal:0, grav_10:0, grav_5:0, iva_10:0, iva_5:0, exento:0, bas_imp:0, no_imputable:0, total_fisica:0 });

  const TIPO_OPTS = { oc_materiales: "OC Materiales", ots_servicios: "OTS Servicios", caja_chica: "Caja Chica" };
  const hasFilters = filters.desde || filters.hasta || filters.project_id || filters.tipo || filters.cod_cliente || filters.item_text || filters.nro_factura || filters.timbrado;

  const clearAll = () => {
    const vacios = { desde: "", hasta: "", project_id: "", tipo: "", cod_cliente: "", item_text: "", nro_factura: "", timbrado: "" };
    setFilters(vacios);
    setSelectedMonth("");
    setSelectedYear("");
    // Fix EB (2026-09-23): el setTimeout recargaba con los filtros viejos (quedaban en el cierre).
    fetchData(vacios);
  };

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{titulo}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {esLibro ? "Registro contable de compras · IVA Paraguayo (DNIT)" : "Compras de materiales, servicios y caja chica"} · {entries.length} {esLibro ? "facturas" : "registros"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchData()}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            data-testid="refresh-libro-btn"
            title="Recargar"
          >
            <RefreshCw size={16} />
          </button>
          {entries.length > 0 && (
            <button
              onClick={handleExportExcel}
              disabled={exporting}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow-sm"
              data-testid="export-excel-btn"
            >
              {exporting
                ? <Loader2 size={15} className="animate-spin" />
                : <FileSpreadsheet size={15} />}
              Exportar Excel
            </button>
          )}
          {entries.length > 0 && (
            <button
              onClick={() => handleExportPdf()}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
              data-testid="export-pdf-btn"
            >
              <FileText size={15} />
              Exportar PDF
            </button>
          )}
          {entries.length > 0 && (
            <button
              onClick={() => handleExportPdf(true)}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
              data-testid="export-preview-btn"
            >
              <Eye size={15} />
              Vista previa
            </button>
          )}
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Subtotal (sin IVA)", value: totals.subtotal,     color: "text-gray-800",    bg: "bg-white" },
          { label: "Grav. 10%",          value: totals.grav_10,      color: "text-[#2E6DA4]",   bg: "bg-[#2E6DA4]/5" },
          { label: "IVA 10% (crédito)",  value: totals.iva_10,       color: "text-orange-600",  bg: "bg-orange-50" },
          { label: "Total Compras",       value: totals.total_fisica, color: "text-[#1E3A5F]",   bg: "bg-[#1E3A5F]/5" },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-2xl border border-gray-100 p-4 shadow-sm`}>
            <p className="text-xs text-gray-500 font-medium mb-1">{s.label}</p>
            <p className={`text-lg font-bold ${s.color}`}>Gs. {fmtNum(Math.round(s.value))}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} className="text-gray-400" />
          <span className="text-sm font-semibold text-gray-700">Filtros</span>
          {hasFilters && (
            <button
              onClick={clearAll}
              className="ml-auto flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-lg px-2 py-1 hover:bg-gray-50"
              data-testid="clear-filters-btn"
            >
              <X size={11} /> Limpiar
            </button>
          )}
        </div>

        {/* Row 1: Month quick selector */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="text-xs font-medium text-gray-500 self-center">Mes rápido:</span>
          <div className="flex gap-1.5 flex-wrap">
            <select
              value={selectedYear}
              onChange={e => { setSelectedYear(e.target.value); if (selectedMonth && e.target.value) applyMonthFilter(selectedMonth, e.target.value); }}
              className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none"
              data-testid="filter-year-select"
            >
              <option value="">Año</option>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            {MONTHS.map(m => (
              <button
                key={m.value}
                onClick={() => {
                  const yr = selectedYear || String(currentYear);
                  setSelectedMonth(m.value);
                  setSelectedYear(yr);
                  applyMonthFilter(m.value, yr);
                }}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${selectedMonth === m.value && (selectedYear || String(currentYear)) ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-200 hover:border-[#2E6DA4] hover:text-[#2E6DA4]"}`}
                data-testid={`month-btn-${m.value}`}
              >
                {m.label.slice(0, 3)}
              </button>
            ))}
          </div>
        </div>

        {/* Row 2: Range + Project + Tipo + Apply */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input
              type="date" value={filters.desde}
              onChange={e => { setFilters(f => ({ ...f, desde: e.target.value })); setSelectedMonth(""); }}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="filter-desde"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input
              type="date" value={filters.hasta}
              onChange={e => { setFilters(f => ({ ...f, hasta: e.target.value })); setSelectedMonth(""); }}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="filter-hasta"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Proyecto / Obra</label>
            {/* Fix EH (2026-09-25): "es muy difícil encontrar rápidamente lo que uno busca en
                la lista desplegada, son demasiados trabajos" (CEN, 25/09). Mismo valor que el
                <select>; se busca por código, cliente u obra, sin acentos y en cualquier orden. */}
            <SelectBuscable
              value={filters.project_id}
              onChange={v => setFilters(f => ({ ...f, project_id: v }))}
              options={projects.map(p => ({ value: p._id, label: `${p.code} · ${p.client || "—"} · ${p.name}` }))}
              vacio="Todas las obras"
              placeholderBuscar="Buscar código, cliente u obra..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              testId="filter-project"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Tipo</label>
            <select
              value={filters.tipo}
              onChange={e => setFilters(f => ({ ...f, tipo: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="filter-tipo"
            >
              <option value="">Todos</option>
              {Object.entries(TIPO_OPTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <button
              onClick={() => fetchData()}
              className="w-full flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
              data-testid="apply-filter-btn"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Filter size={14} />}
              Aplicar
            </button>
          </div>
        </div>

        {/* Row 3: Cod cliente + Item + Nro Factura + Timbrado */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Cod. Cliente</label>
            <input
              type="text" value={filters.cod_cliente}
              onChange={e => setFilters(f => ({ ...f, cod_cliente: e.target.value }))}
              placeholder="Ej: CL-T001 o T001"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="filter-cod-cliente"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Ítem (descripción)</label>
            <input
              type="text" value={filters.item_text}
              onChange={e => setFilters(f => ({ ...f, item_text: e.target.value }))}
              placeholder="Cable, llave, bandeja..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="filter-item-text"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Nro Factura</label>
            <input
              type="text" value={filters.nro_factura}
              onChange={e => setFilters(f => ({ ...f, nro_factura: e.target.value }))}
              placeholder="001-001-1234567"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="filter-nro-factura"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Nro Timbrado</label>
            <input
              type="text" value={filters.timbrado}
              onChange={e => setFilters(f => ({ ...f, timbrado: e.target.value }))}
              placeholder="15234567"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="filter-timbrado"
            />
          </div>
        </div>

        {/* Active filter badge */}
        {filters.project_id && (() => {
          const proj = projects.find(p => p._id === filters.project_id);
          return proj ? (
            <div className="mt-2 flex items-center gap-1.5">
              <span className="text-xs text-[#2E6DA4] bg-[#2E6DA4]/10 px-2.5 py-0.5 rounded-full font-medium">
                Obra: {proj.name}
              </span>
            </div>
          ) : null;
        })()}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 rounded-xl" />)}
        </div>
      ) : entries.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <BookOpen size={44} className="mx-auto text-gray-200 mb-4" />
          <p className="text-gray-500 font-medium">No hay registros para los filtros seleccionados</p>
          <p className="text-xs text-gray-400 mt-1">OC aprobadas, OTS y gastos de Caja Chica aparecen aquí</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            {/* Fix FA (2026-10-06): el Informe muestra la gestión interna (por ítem) y el Libro
                solo los datos contables, una fila por factura. */}
            <table className="text-[11px] border-collapse" data-testid="libro-table" style={{ minWidth: esLibro ? "1900px" : "1420px" }}>
              <thead>
                <tr>
                  <th
                    colSpan={COLS.length}
                    className={`${esLibro ? "bg-[#1E3A5F]" : "bg-[#2E6DA4]"} text-white text-center font-bold py-2 px-4 tracking-wider text-xs`}
                    style={{ letterSpacing: "0.08em" }}
                  >
                    {esLibro ? "DATOS CONTABLES DE LAS FACTURAS" : "DATOS DE GESTIÓN INTERNA"}
                  </th>
                </tr>
                <tr>
                  {COLS.map((col, i) => (
                    <th
                      key={col.key}
                      className={`${esLibro ? "bg-[#1E3A5F]/90 text-white border-white/20" : "bg-[#D6E4F0] text-[#1E3A5F] border-[#2E6DA4]/30"} font-bold py-2 px-2 whitespace-nowrap border-b-2 text-[10px] ${
                        col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
                      } ${i === 0 ? "sticky left-0 z-10" : ""}`}
                      style={{ letterSpacing: "0.04em" }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {entries.map((e, idx) => (
                  <tr
                    key={idx}
                    className={`hover:bg-[#2E6DA4]/5 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}
                    data-testid="libro-row"
                  >
                    {COLS.map((col, ci) => {
                      const val = e[col.key];
                      let display;
                      if (col.currency) {
                        display = val ? `Gs. ${fmtNum(Math.round(val))}` : "—";
                      } else if (col.number) {
                        display = val ? fmtNum(val) : "—";
                      } else if (col.key === "orden_compra") {
                        // BLOQUE 51 PARTE B — saldo pendiente de facturar de la OC.
                        display = (
                          <span className="inline-flex items-center gap-1">
                            {val || "—"}
                            {e.oc_pending_total > 0 && (
                              <span
                                className="text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-300 rounded px-1 py-0.5 whitespace-nowrap"
                                title={`Falta facturar Gs. ${fmtNum(Math.round(e.oc_pending_total))}`}
                                data-testid={`libro-falta-facturar-${e.oc_id}`}
                              >
                                Falta Gs.{fmtNum(Math.round(e.oc_pending_total))}
                              </span>
                            )}
                          </span>
                        );
                      } else if (col.key === "nro_factura") {
                        // BLOQUE 49 — ícono de ver el documento real de la factura
                        display = (
                          <span className="inline-flex items-center gap-1">
                            {val || "—"}
                            {e.doc_available && (
                              <button
                                onClick={() => window.open(`${API}/purchases/oc-from-pf/${e.oc_id}/invoices/${e.invoice_id}/document?inline=1`, "_blank")}
                                className="text-[#2E6DA4] hover:text-[#1E3A5F]" title="Ver documento de la factura"
                                data-testid={`libro-view-doc-${e.oc_id}-${e.invoice_id}`}
                              >
                                <Eye size={11} />
                              </button>
                            )}
                          </span>
                        );
                      } else if (col.key === "factura_vinculada") {
                        display = (
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${String(val).startsWith("Sí") ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                            data-testid="libro-factura-vinculada">
                            {val || "No"}
                          </span>
                        );
                      } else {
                        display = fmtDate(val) || "—";
                      }
                      return (
                        <td
                          key={col.key}
                          className={`px-2 py-2 ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"} ${
                            col.mono ? "font-mono" : ""
                          } text-gray-700 ${ci === 0 ? "sticky left-0 z-10 bg-inherit font-semibold text-[#2E6DA4]" : ""}`}
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* TOTALS row */}
                <tr className="bg-[#EBF5FB] border-t-2 border-[#2E6DA4]/30 font-bold" data-testid="libro-totales">
                  {COLS.map((col, ci) => (
                    <td key={col.key} className={`px-2 py-2.5 text-[#1E3A5F] font-bold text-xs whitespace-nowrap ${col.currency ? "text-right font-mono" : ""} ${ci === 0 ? "sticky left-0 z-10 bg-[#EBF5FB]" : ""}`}>
                      {ci === 0 ? `TOTALES — ${entries.length} ${esLibro ? "facturas" : "registros"}`
                        : col.currency ? (t => (Math.round(t) ? `Gs. ${fmtNum(Math.round(t))}` : "—"))(entries.reduce((s2, x) => s2 + (Number(x[col.key]) || 0), 0)) : ""}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
        {Object.entries(TIPO_COLORS).map(([label, cls]) => (
          <span key={label} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>{label}</span>
        ))}
        <span className="text-gray-400">· Todos los montos en Guaraníes (PYG) · IVA 10% incluido</span>
      </div>
    </div>
  );
}
